export const CLICK_ID_COOKIE_NAME = "upsider_click";
export const CLICK_ID_COOKIE_MAX_AGE = 60 * 60 * 24 * 180;
export const CLICK_ID_MAX_VALUE_LENGTH = 256;
export const CLICK_ID_COOKIE_MAX_ENCODED_LENGTH = 2000;

export const CLICK_ID_KEYS = [
  "gclid",
  "gbraid",
  "wbraid",
  "fbclid",
  "msclkid",
  "twclid",
  "ttclid",
  "li_fat_id",
  "rdt_cid",
] as const;

export const UTM_KEYS = [
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_term",
  "utm_content",
] as const;

export type ClickIds = Partial<Record<(typeof CLICK_ID_KEYS)[number], string>>;

export function clickIdsFromSearchParams(params: URLSearchParams): ClickIds {
  const ids: ClickIds = {};
  for (const key of CLICK_ID_KEYS) {
    const value = params.get(key)?.trim();
    if (value && value.length <= CLICK_ID_MAX_VALUE_LENGTH) ids[key] = value;
  }
  return ids;
}

export function serializeClickIdCookie(ids: ClickIds): string | null {
  const value = JSON.stringify(ids);
  return encodeURIComponent(value).length <= CLICK_ID_COOKIE_MAX_ENCODED_LENGTH ? value : null;
}

export function parseClickIdCookie(raw: string | null | undefined): ClickIds {
  if (!raw) return {};
  try {
    const parsed: unknown = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object") return {};
    const ids: ClickIds = {};
    for (const key of CLICK_ID_KEYS) {
      const value = (parsed as Record<string, unknown>)[key];
      if (typeof value === "string" && value.trim() && value.trim().length <= CLICK_ID_MAX_VALUE_LENGTH) {
        ids[key] = value.trim();
      }
    }
    return ids;
  } catch {
    return {};
  }
}

export const FIRST_TOUCH_COOKIE_NAME = "upsider_first_touch";
export const LAST_TOUCH_COOKIE_NAME = "upsider_last_touch";
export const LANDING_MAX_VALUE_LENGTH = 600;
const LANDING_MAX_PATHNAME_LENGTH = 100;
const LANDING_MAX_REFERRER_LENGTH = 300;

export type LandingCookie = { path: string; referrer?: string; ts: number };

const LANDING_PARAM_KEYS = [...CLICK_ID_KEYS, "via", ...UTM_KEYS];

const INTERNAL_DOMAINS = new Set([
  "upside.tools",
  "upsider.ai",
  "www.upsider.ai",
  "app.upsider.ai",
  "checkout.stripe.com",
  "billing.stripe.com",
  "app.link.com",
  "localhost",
  "127.0.0.1",
  "::1",
]);

export function isInternalDomain(domain: string): boolean {
  const cleanDomain = domain.replace(/^www\./, "").toLowerCase();
  return (
    INTERNAL_DOMAINS.has(cleanDomain) ||
    cleanDomain.endsWith(".upsider.ai") ||
    cleanDomain.endsWith(".upside.tools")
  );
}

function usableParam(params: URLSearchParams, key: string): string | null {
  const value = params.get(key)?.trim();
  return value && value.length <= CLICK_ID_MAX_VALUE_LENGTH ? value : null;
}

function landingParams(base: string, params: URLSearchParams): string {
  const kept = new URLSearchParams();
  for (const key of LANDING_PARAM_KEYS) {
    const value = usableParam(params, key);
    if (!value) continue;
    kept.set(key, value);
    if (base.length + 1 + kept.toString().length > LANDING_MAX_VALUE_LENGTH) kept.delete(key);
  }
  return kept.toString();
}

export function isTaggedLanding(pathname: string, params: URLSearchParams): boolean {
  return landingParams(pathname.slice(0, LANDING_MAX_PATHNAME_LENGTH), params) !== "";
}

export function isExternalReferrer(referrer: string | null, ownHost: string): boolean {
  if (!referrer) return false;
  try {
    const host = new URL(referrer).hostname;
    return host !== ownHost && !isInternalDomain(host);
  } catch {
    return false;
  }
}

export function landingPath(pathname: string, params: URLSearchParams): string {
  const base = pathname.slice(0, LANDING_MAX_PATHNAME_LENGTH);
  const query = landingParams(base, params);
  return query ? `${base}?${query}` : base;
}

export function boundedLandingPath(path: string): string {
  if (path.length <= LANDING_MAX_VALUE_LENGTH) return path;
  const end = Math.max(
    path.lastIndexOf("&", LANDING_MAX_VALUE_LENGTH),
    path.lastIndexOf("?", LANDING_MAX_VALUE_LENGTH),
  );
  return path.slice(0, end > 0 ? end : LANDING_MAX_VALUE_LENGTH);
}

export function serializeLandingCookie(touch: LandingCookie): string {
  return JSON.stringify({
    p: boundedLandingPath(touch.path),
    ...(touch.referrer ? { r: touch.referrer.slice(0, LANDING_MAX_REFERRER_LENGTH) } : {}),
    t: touch.ts,
  });
}

export function parseLandingCookie(raw: string | null | undefined): LandingCookie | null {
  if (!raw) return null;
  try {
    const parsed: unknown = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object") return null;
    const { p, r, t } = parsed as Record<string, unknown>;
    if (typeof p !== "string" || !p.startsWith("/") || p.startsWith("//") || p.length > LANDING_MAX_VALUE_LENGTH) return null;
    if (typeof t !== "number" || !Number.isFinite(t) || t <= 0) return null;
    if (r !== undefined && (typeof r !== "string" || r.length > LANDING_MAX_REFERRER_LENGTH)) return null;
    return { path: p, ...(r ? { referrer: r } : {}), ts: t };
  } catch {
    return null;
  }
}
